import classNames from 'classnames'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { Button } from 'components/Button/Button'
import { MyDropdown } from 'components/ReactHookForm/MyDropdown'
import {
  ClaimDateRange,
  ClaimsFiltersForm,
  createSchema,
  defaultClaimsFiltersForm,
  getClaimsSearchRequest,
  useClaimTypeOptions
} from 'models/claims'
import {
  getFilters,
  getSearchResultsTotal,
  resetDateRange,
  resetFilters,
  searchClaims,
  setFilters
} from 'redux/reducers/claims'
import { ClaimsFiltersAlert } from '../components/ClaimsFiltersAlert'
import { ClaimsSort } from './ClaimsSort'
import { MyDateRange } from 'components/MyDateRange/MyDateRange'
import { useMountedState } from 'hooks/useMountedState'
import { zodResolver } from '@hookform/resolvers/zod'
import { useHookFormErrors } from 'hooks/useHookFormErrors'
import { Component } from '@cms-max/medicare-api-sdk'

// TODO: what should be the earliest date a user can select?
export const CLAIM_CUSTOM_DATE_START_YEAR = 1965

const sharedButtonClasses = [
  'ds-c-button',
  'ds-u-padding-y--2',
  'ds-u-padding-x--4',
  'my-u-lg-position--relative',
  'my-u-lg-top--28',
  'ds-u-margin-top--3',
  'ds-u-margin-bottom--1',
  'ds-u-lg-margin-top--0'
]

export interface ClaimsFiltersProps {
  className?: string
}

export const ClaimsFilters: FC<ClaimsFiltersProps> = ({ className }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('claimsSearch')
  const filters = useSelector(getFilters)
  const claimTypeOpts = useClaimTypeOptions()
  const totalResults = useSelector(getSearchResultsTotal)
  const hasResults = totalResults > 0
  const [isSearching, setIsSearching] = useMountedState(false)

  const handleSubmit: SubmitHandler<ClaimsFiltersForm> = useCallback(
    async (formValues: ClaimsFiltersForm) => {
      setIsSearching(true)
      dispatch(setFilters(formValues))
      const req = getClaimsSearchRequest(formValues)
      await dispatch(searchClaims(req))
      setIsSearching(false)
    },
    [dispatch, setIsSearching]
  )

  const errorRef = useRef<HTMLDivElement>(null)
  const handleError: SubmitErrorHandler<ClaimsFiltersForm> = (errors) => {
    errorRef.current?.focus()
  }

  const schema = useMemo(() => createSchema(t), [t])
  const formMethods = useForm<ClaimsFiltersForm>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      ...filters
    },
    shouldFocusError: false
  })
  const { unregister } = formMethods

  const errorList = useHookFormErrors(formMethods.formState.errors)

  const handleReset = () => {
    dispatch(resetFilters())
    formMethods.reset(defaultClaimsFiltersForm)
    handleSubmit(defaultClaimsFiltersForm)
  }

  const isSubmitted = formMethods.formState.isSubmitted

  const watchDateRange = formMethods.watch('dateRange')

  const [usingCustomRange, setUsingCustomRange] = useState(false)
  useEffect(() => {
    if (watchDateRange) {
      const customDateRange = watchDateRange === ClaimDateRange.CUSTOM
      setUsingCustomRange(customDateRange)
      if (!customDateRange) {
        dispatch(resetDateRange())
        unregister(['customDateRange', 'customDateRange.end', 'customDateRange.start'])
      }
    }
  }, [watchDateRange, dispatch, unregister])

  // Trigger a form submission with the initial and/or stored filter values when the component first renders.
  // Leaving `filters` out of dep array because we don't want to double trigger the submit.
  useEffect(() => {
    handleSubmit(filters)
  }, [handleSubmit]) // eslint-disable-line react-hooks/exhaustive-deps

  const sortClasses = classNames('my-u-gap--6', {
    'my-c-claim-filter--sort-right': usingCustomRange
  })

  return (
    <>
      <section className={classNames('ds-l-container', 'ds-u-visibility--hidden-print', className)}>
        <div className="ds-l-row">
          <div className="ds-l-col--12">
            <h2 className="ds-text-heading--2xl ds-u-border-top--1 ds-u-padding-top--3">
              {t('filterHeading')}
            </h2>
          </div>
        </div>
        <div className="ds-l-row" tabIndex={-1} ref={errorRef}>
          {isSubmitted && Boolean(errorList.length) && (
            <div className="ds-l-col--12 ds-u-margin-top--2">
              <ClaimsFiltersAlert errors={errorList} />
            </div>
          )}
        </div>
        <div className="ds-u-lg-display--flex my-u-gap-x--6 my-u-gap-y--12 ds-u-align-items--start ds-u-flex-wrap--wrap my-u-position--relative">
          <FormProvider {...formMethods}>
            <form
              onSubmit={formMethods.handleSubmit(handleSubmit, handleError)}
              className="ds-u-lg-display--flex ds-u-align-items--start my-u-gap-x--6 my-u-flex-basis--full ds-u-flex-wrap--wrap"
            >
              <MyDropdown
                name="dateRange"
                label={t('filterDateRangeHeading')}
                className="my-c-claim-filter--field my-u-flex-1"
                fieldClassName="ds-u-margin--0"
                labelClassName="ds-c-label ds-u-margin-bottom--1"
                id="claims_filters_dateRange"
                options={[
                  {
                    value: ClaimDateRange.LAST_30_DAYS,
                    label: t('filterClaimRange1Month')
                  },
                  {
                    value: ClaimDateRange.LAST_6_MONTHS,
                    label: t('filterClaimRange6Months')
                  },
                  {
                    value: ClaimDateRange.LAST_12_MONTHS,
                    label: t('filterClaimRange12Months')
                  },
                  {
                    value: ClaimDateRange.LAST_24_MONTHS,
                    label: t('filterClaimRange24Months')
                  },
                  {
                    value: ClaimDateRange.LAST_36_MONTHS,
                    label: t('filterClaimRange36Months')
                  },
                  {
                    value: ClaimDateRange.CUSTOM,
                    label: t('filterClaimRangeCustom')
                  }
                ]}
              />

              {usingCustomRange && (
                <MyDateRange
                  name="customDateRange"
                  label={t('filterClaimDateRange.label')}
                  hint={t('filterClaimDateRange.hint')}
                  className="my-u-lg-order--8"
                  startLabel={t('filterClaimDateRange.labelFrom')}
                  endLabel={t('filterClaimDateRange.labelTo')}
                  startFromYear={CLAIM_CUSTOM_DATE_START_YEAR}
                  startToDate={new Date()}
                  endFromYear={CLAIM_CUSTOM_DATE_START_YEAR}
                  endToDate={new Date()}
                />
              )}

              <MyDropdown
                name="claimType"
                label={t('filterClaimTypeHeading')}
                className="my-c-claim-filter--field my-u-flex-1"
                fieldClassName="ds-u-margin--0"
                labelClassName="ds-c-label ds-u-margin-bottom--1"
                id="claims_filters_claimType"
                options={[
                  { value: '', label: t('filterClaimTypeAll') },
                  ...claimTypeOpts
                ]}
                defaultValue=''
              />

              <MyTextField
                name="claimNumber"
                className="my-c-claim-filter--field my-u-flex-1"
                fieldClassName="ds-u-margin--0"
                label={`${t('filterClaimNumberHeader')}`}
                labelClassName="ds-c-label ds-u-margin-bottom--1"
                placeholder={`${t('filterClaimNumberPlaceholder')}`}
                defaultValue=''
              />
              <div className='my-u-flex-1'>
                <Button
                  className={classNames(sharedButtonClasses, ['ds-c-button--solid ds-c-button--alternate'])}
                  type="submit"
                  disabled={isSearching}
                  siteActivityProps={{
                    core: {
                      component: Component.Claims,
                      elementId: 'claims_claimsearch_click',
                      coreClickData: {
                        linkText: t('filterApplyButton')
                      }
                    }
                  }}
                >
                  {t('filterApplyButton')}
                </Button>
                <Button
                  className={classNames(sharedButtonClasses, ['ds-u-margin-left--1'])}
                  disabled={isSearching}
                  onClick={handleReset}
                  siteActivityProps={{
                    core: {
                      component: Component.Claims,
                      elementId: 'claims_claimsearchreset_click',
                      coreClickData: {
                        linkText: t('filterResetButton')
                      }
                    }
                  }}
                >
                  {t('filterResetButton')}
                </Button>
              </div>
            </form>
          </FormProvider>

          {hasResults && <ClaimsSort className={sortClasses} />}
        </div>
      </section>
    </>
  )
}
